import { useEffect, useMemo, useState } from 'react';
import { categoriesApi } from '@/lib/api/arrex/catalog';
import { ApiError } from '@/lib/api/client';
import { useApp } from '@/contexts/AppContext';
import { localized, imgUrl } from '@/lib/format';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ToggleField } from '@/components/ui/toggle-field';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Activity, Loader2, LayoutGrid } from 'lucide-react';
import { ImageUploader } from '@/components/admin/ImageUploader';
import { PickerSelect } from '@/components/admin/PickerSelect';
import { useLocation } from 'react-router-dom';
import { readItems } from '@/lib/api/normalize';
import { CardGridSkeleton } from '@/components/ui/skeletons';
import AdminHomeCollections from './AdminCollections';

const empty = {
  name_en: '', name_ar: '', slug: '', image: '',
  sort_order: 0, is_active: true, parent_id: null as string | null, status: 'active',
};

export default function AdminCategories({ subOnly = false }: { subOnly?: boolean }) {
  const { t } = useTranslation();
  const { lang } = useApp();
  const location = useLocation();
  const isSub = subOnly || location.pathname.endsWith('/subcategories');
  const [cats, setCats] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<any>(empty);
  const [saving, setSaving] = useState(false);
  const [parentFilter, setParentFilter] = useState<string>('all');
  const [deleteTarget, setDeleteTarget] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => categoriesApi.adminList()
    .then((res) => setCats(readItems(res)))
    .catch(() => setCats([]))
    .finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const mains = useMemo(() => cats.filter(c => !c.parent_id), [cats]);
  const visible = useMemo(() => {
    let list = isSub ? cats.filter(c => c.parent_id) : cats.filter(c => !c.parent_id);
    if (isSub && parentFilter !== 'all') list = list.filter(c => c.parent_id === parentFilter);
    return list;
  }, [cats, isSub, parentFilter]);


  const save = async () => {
    if (!form.name_en || !form.slug) return toast.error('Name and slug required');
    if (isSub && !form.parent_id) return toast.error('Parent category is required');
    setSaving(true);
    const payload = {
      nameEn: form.name_en, nameAr: form.name_ar || form.name_en,
      slug: form.slug, image: form.image || null,
      sortOrder: Number(form.sort_order) || 0, isActive: !!form.is_active,
      parentId: isSub ? form.parent_id : null,
      status: form.status || 'active',
    };
    try {
      if (form.id) await categoriesApi.update(form.id, payload);
      else await categoriesApi.create(payload);
      toast.success(form.id ? 'Updated' : 'Created');
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await categoriesApi.remove(deleteTarget.id);
      toast.success('Deleted');
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const startNew = () => setForm({ ...empty, parent_id: isSub ? (parentFilter !== 'all' ? parentFilter : null) : null });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <h1 className="font-display text-3xl font-bold">
          {isSub ? t('admin.subcategories') : t('admin.categories.title')}
        </h1>
        <div className="flex items-center gap-2">
          {isSub && (
            <Select value={parentFilter} onValueChange={setParentFilter}>
              <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('shop.allCategories')}</SelectItem>
                {mains.map(m => <SelectItem key={m.id} value={m.id}>{localized(m, 'name', lang)}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Button onClick={() => { startNew(); setOpen(true); }}><Plus className="w-4 h-4 me-1" />{isSub ? t('admin.addSubcategory') : t('admin.addCategory')}</Button>
        </div>
      </div>

      {loading ? <CardGridSkeleton count={6} /> : (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map(c => {
          const parent = cats.find(p => p.id === c.parent_id);
          return (
            <div key={c.id} className="surface-card rounded-lg p-4 flex gap-3 items-center">
              {c.image && <img src={imgUrl(c.image)} alt="" className="w-16 h-16 rounded object-cover" />}
              <div className="flex-1 min-w-0">
                <p className="font-medium break-words">{localized(c, 'name', lang)}</p>
                <p className="text-xs text-muted-foreground font-mono">{c.slug}</p>
                <div className="flex flex-wrap gap-1 mt-1">
                  <Badge variant="outline" className="text-xs">{c.is_active ? 'Active' : 'Hidden'}</Badge>
                  {c.status === 'coming_soon' && <Badge className="text-xs bg-primary text-primary-foreground">{t('admin.common.comingSoon')}</Badge>}
                  {parent && <Badge variant="secondary" className="text-xs">{localized(parent, 'name', lang)}</Badge>}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <Button size="icon" variant="ghost" onClick={() => { setForm({ ...c }); setOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(c)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </div>
          );
        })}
      {visible.length === 0 && <p className="text-muted-foreground">No {isSub ? 'subcategories' : 'categories'} yet.</p>}
      </div>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Curated Collections — only shown on the main categories page        */}
      {/* ------------------------------------------------------------------ */}
      {!isSub && (
        <div className="mt-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-border" />
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted text-muted-foreground text-xs font-medium uppercase tracking-wider shrink-0">
              <LayoutGrid className="w-3.5 h-3.5" />
              {t('admin.nav.collections')}
            </div>
            <div className="flex-1 h-px bg-border" />
          </div>
          <AdminHomeCollections embedded />
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{form.id ? t('admin.edit') : (isSub ? t('admin.addSubcategory') : t('admin.addCategory'))}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('admin.common.nameEn')}</Label>
              <Input
                value={form.name_en}
                onChange={e => {
                  const val = e.target.value;
                  const generatedSlug = val.toLowerCase().replace(/[^a-z0-9_-]+/g, '-').replace(/(^-|-$)/g, '');
                  setForm({
                    ...form,
                    name_en: val,
                    slug: form.id ? form.slug : generatedSlug
                  });
                }}
              />
            </div>
            <div><Label>{t('admin.common.nameAr')}</Label><Input value={form.name_ar} onChange={e => setForm({ ...form, name_ar: e.target.value })} /></div>
            <div className="col-span-2"><Label>{t('admin.common.slug')}</Label><Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} /></div>
            {isSub && (
              <div className="col-span-2">
                <Label>{t('admin.common.parentCategory')}</Label>
                <div className="mt-1">
                  <PickerSelect
                    tone="primary"
                    placeholder={t('admin.selectMainCategory')}
                    options={mains.map(m => ({ id: m.id, label: localized(m, 'name', lang) }))}
                    value={form.parent_id || null}
                    onChange={(v) => setForm({ ...form, parent_id: v })}
                  />
                </div>
              </div>
            )}
            <div>
              <Label>{t('admin.common.status')}</Label>
              <div className="mt-1">
                <PickerSelect
                  tone="primary"
                  placeholder={t('admin.common.selectStatus')}
                  icon={Activity}
                  options={[
                    { id: 'active', label: 'Active' },
                    { id: 'coming_soon', label: 'Coming Soon' },
                  ]}
                  value={form.status || 'active'}
                  onChange={(v) => setForm({ ...form, status: v })}
                />
              </div>
            </div>
            <div><Label>{t('admin.common.sortOrder')}</Label><Input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: e.target.value })} /></div>
            <div className="mt-6 col-span-2">
              <ToggleField
                checked={!!form.is_active}
                onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                label={t('admin.common.visibleActive')}
                labelPosition="end"
                variant="field"
              />
            </div>
            <div className="col-span-2">
              <Label>{t('admin.common.image')}</Label>
              <div className="mt-1">
                <ImageUploader
                  value={form.image}
                  onChange={(url) => setForm({ ...form, image: url })}
                  bucket="category-images"
                  aspect="category"
                  responsivePreview
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t('admin.cancel')}</Button>
            <Button onClick={save} disabled={saving}>{t('admin.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="w-5 h-5" />
              {isSub ? t('admin.deleteSubcategory') : t('admin.deleteCategory')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && t('admin.deleteCategoryConfirm', { name: localized(deleteTarget, 'name', lang) })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>{t('admin.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : t('admin.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
